import { setDate } from 'date-fns';
import compact from 'lodash/compact';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useRef, useEffect } from 'react';
import { useFela } from 'react-fela';
import { IDatePicker, IWedding_Website } from '@bridebook/models/source/models/Weddings.types';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import {
  useViewModeEditMode,
  useViewModeSuppliers,
  useViewModeWedding,
} from '@bridebook/web/components/wedding-website/view-mode/view-mode.context';
import {
  DATE_FORMAT,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { PublicUrls, UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import styles from './wedding-website-overview.style';
import {
  useGetWeddingWebsiteDateFormat,
  WeddingWebsiteDateFormat,
} from '@bridebook/web/lib/wedding-website/hooks/use-get-wedding-website-date-format';
import { A } from '@bridebook/ui';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useRouter } from 'next/router';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';

interface IProps {
  websiteId?: string;
  palette?: Palettes;
  primaryVenueId?: string;
  config: IWedding_Website['config'];
}

const formatDatePickerToReadable = (dp: IDatePicker, shouldUseOrdinals: boolean) =>
  format(
    setDate(new Date(Number(dp.y), Number(dp.m), 1), Number(dp.d)),
    shouldUseOrdinals ? DATE_FORMAT.DATE.CALENDAR.LONGEST : DATE_FORMAT.DATE.WITH_DAY,
  );

export const WeddingWebsiteOverview: FC<IProps> = ({
  websiteId,
  palette,
  config,
  primaryVenueId,
}) => {
  const { t } = useTranslation('weddingWebsite');
  const { css } = useFela();
  const dispatch = useDispatch();
  const format = useGetWeddingWebsiteDateFormat();
  // probably add some kind of hook
  const { date, partners } = useViewModeWedding();
  const editMode = useViewModeEditMode();
  const { byId } = useViewModeSuppliers();
  const [name, partnerName] = partners;
  const missingPartnerName = t('partners.missingNo');
  const names = [name || missingPartnerName, partnerName || missingPartnerName].join(' & ');
  const market = useMarket();
  const router = useRouter();
  const { triggerDetailsSync, registerPreviewRef } = useEditorSyncContext();

  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('overview', tileRef.current);
  }, [registerPreviewRef]);

  const guestNameFromQuery = router.query.guest as string | undefined;
  const guestIdFromQuery = router.query.guestId as string | undefined;
  const handleEditDateAndLocationClick = useCallback(() => {
    triggerDetailsSync('overview');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'rsvp',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);

  const handleRSVPClick = useCallback(() => {
    if (editMode) {
      return;
    }
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'rsvp',
        element: 'RSVP',
      }),
    );
  }, [dispatch, editMode]);

  const mainSupplier = primaryVenueId ? byId[primaryVenueId] : undefined;

  const formattedDate =
    date && isDateExact(date)
      ? // french is expected to not use ordinals, TODO: use a tool that can properly parse dates in all language such as moment-tz or luxon
        formatDatePickerToReadable(date, format === WeddingWebsiteDateFormat.Ordinal)
      : undefined;
  const formattedAddress = mainSupplier
    ? compact([mainSupplier.name, mainSupplier.address?.city, mainSupplier.address?.country]).join(
        ', ',
      )
    : undefined;

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent
        title={<div className={css(styles.h1)}>{names}</div>}
        palette={palette ?? Palettes.Bridebook}
        variant={WebsiteTileVariant.Primary}>
        <WeddingWebsiteTileComponent.Container>
          <WeddingWebsiteTileComponent.ItemGroup>
            <WeddingWebsiteTileComponent.Title>
              {formattedDate ?? t('previewMode.tile.overview.dateToBeAnnounced')}
            </WeddingWebsiteTileComponent.Title>
            <WeddingWebsiteTileComponent.Text>
              {formattedAddress ?? t('previewMode.tile.overview.venueToBeAnnounced')}
            </WeddingWebsiteTileComponent.Text>
          </WeddingWebsiteTileComponent.ItemGroup>
          {config.rsvpVisible && (
            <A
              onChange={handleRSVPClick}
              className="self-center"
              href={
                websiteId
                  ? `/${market.prefix}${PublicUrls.for.rsvp(websiteId, { guest: guestNameFromQuery, guestId: guestIdFromQuery })}`
                  : ''
              }
              target="_blank">
              <WeddingWebsiteTileComponent.Button>
                {t('previewMode.tile.overview.cta')}
              </WeddingWebsiteTileComponent.Button>
            </A>
          )}
        </WeddingWebsiteTileComponent.Container>
        <FloatingEditButton
          href={UrlHelper.website.weddingDetails.main()}
          onClick={handleEditDateAndLocationClick}
        />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
